import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";

export const isNativePhoneAuthAvailable = () => Capacitor.isNativePlatform();

let autoVerifiedSession = null;
let autoVerificationListener = null;

const cleanPhone = (value = "") => String(value || "").replace(/\s+/g, "").trim();

const watchAutomaticVerification = async (phoneNumber) => {
  autoVerifiedSession = null;
  await autoVerificationListener?.remove?.().catch(() => undefined);
  autoVerificationListener = await FirebaseAuthentication.addListener("phoneVerificationCompleted", (event) => {
    const verificationCode = String(event?.verificationCode || "").trim();
    if (!verificationCode) return;
    autoVerifiedSession = { phoneNumber: cleanPhone(phoneNumber), verificationCode };
  });
};

export const sendNativePhoneCode = async (phoneNumber) => {
  if (!isNativePhoneAuthAvailable()) throw new Error("Native phone authentication is not available.");

  await watchAutomaticVerification(phoneNumber);

  return new Promise(async (resolve, reject) => {
    let settled = false;
    const handles = [];

    const finish = async (callback, value) => {
      if (settled) return;
      settled = true;
      await Promise.all(handles.map((handle) => handle.remove().catch(() => undefined)));
      callback(value);
    };

    try {
      handles.push(await FirebaseAuthentication.addListener("phoneCodeSent", ({ verificationId }) => {
        finish(resolve, verificationId);
      }));
      handles.push(await FirebaseAuthentication.addListener("phoneVerificationFailed", ({ message }) => {
        finish(reject, new Error(message || "Phone verification could not be started."));
      }));
      await FirebaseAuthentication.signInWithPhoneNumber({
        phoneNumber,
        // Firebase performs Play Integrity verification natively, while the
        // resulting SMS credential is consumed by the web SDK below. This
        // keeps one persistent Firebase session for the whole application.
        skipNativeAuth: true
      });
    } catch (error) {
      finish(reject, error);
    }
  });
};

export const confirmNativePhoneCode = async (verificationId, verificationCode, phoneNumber) => {
  const expectedPhone = cleanPhone(phoneNumber);
  const resolvedCode = autoVerifiedSession?.phoneNumber === expectedPhone
    ? autoVerifiedSession.verificationCode
    : String(verificationCode || "").trim();
  if (!verificationId || !resolvedCode) throw new Error("Enter the verification code sent to your phone.");
  autoVerifiedSession = null;
  return { verificationId, verificationCode: resolvedCode };
};
